import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const PRINT_STYLES = `
@media print {
  @page { size: A4 portrait; margin: 16mm 14mm; }
  .print-hide { display: none !important; }
  body { background: #fff !important; }
  #meeting-minutes-print table { page-break-inside: auto; }
  #meeting-minutes-print tr { page-break-inside: avoid; break-inside: avoid; }
}
`;

const STATUS_LABEL = {
  pending: 'Pending',
  completed: 'Completed',
  deadline_extended: 'Deadline Extended',
};

function fmtDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`));
}

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

export default function MeetingMinutesPrintView() {
  const { id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    axiosInstance.get(`/admin/meetings/${id}`)
      .then((resp) => { if (isMounted) setMeeting(resp.data?.meeting || null); })
      .catch((err) => { if (isMounted) setError(err?.response?.data?.message || 'Failed to load meeting.'); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, [id]);

  useEffect(() => {
    if (loading || error || !meeting) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [loading, error, meeting]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-gray-400">Loading…</div>;
  }
  if (error || !meeting) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-red-500">{error || 'Meeting not found.'}</div>;
  }

  const allActionItems = meeting.actionItems;
  const recordedBy = AuthService.getUser()?.superAdmin?.staff?.name || 'Super Admin';

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="min-h-screen bg-gray-100">
        <div className="print-hide sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-600">Print preview — Meeting Minutes</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => window.print()} className="rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700">
              Print
            </button>
            <button type="button" onClick={() => window.close()} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>

        <div id="meeting-minutes-print" className="mx-auto max-w-3xl bg-white px-10 py-8 print:max-w-none">
          <div className="border-b-2 border-slate-800 pb-4 text-center">
            <h1 className="text-xl font-bold uppercase tracking-wide text-slate-900">Meeting Minutes</h1>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-slate-700">
            <p><span className="font-semibold text-slate-500">Meeting:</span> {meeting.title}</p>
            <p><span className="font-semibold text-slate-500">Date:</span> {fmtDate(meeting.meetingDate)}{meeting.meetingTime ? ` · ${to12h(meeting.meetingTime)}` : ''}</p>
            <p><span className="font-semibold text-slate-500">Location:</span> {meeting.location || '—'}</p>
            <p><span className="font-semibold text-slate-500">Chaired By:</span> {meeting.chairedByName || '—'}</p>
          </div>

          <div className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-800">Attendance ({meeting.attendees.filter((a) => a.attended === 1).length}/{meeting.attendees.length} present)</h2>
            <table className="mt-2 w-full border-collapse text-xs">
              <thead>
                <tr className="border border-slate-300 bg-slate-50">
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Name</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Employee No.</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {meeting.attendees.map((a) => (
                  <tr key={a.staffId}>
                    <td className="border border-slate-300 px-2 py-1.5">{a.name}</td>
                    <td className="border border-slate-300 px-2 py-1.5">{a.employeeNumber || '—'}</td>
                    <td className="border border-slate-300 px-2 py-1.5 text-center">
                      {a.attended === 1 ? 'Present' : a.attended === 0 ? 'Absent (Apologies)' : 'Unmarked'}
                    </td>
                  </tr>
                ))}
                {meeting.attendees.length === 0 && (
                  <tr><td colSpan={3} className="border border-slate-300 px-2 py-3 text-center text-slate-400">No attendees recorded.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-800">Agenda &amp; Action Items</h2>
            <table className="mt-2 w-full border-collapse text-xs">
              <thead>
                <tr className="border border-slate-300 bg-slate-50">
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Description</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Assigned To</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Status</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Deadline</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Remark</th>
                </tr>
              </thead>
              <tbody>
                {allActionItems.map((item) => (
                  <tr key={item.id}>
                    <td className="border border-slate-300 px-2 py-1.5">{item.description}</td>
                    <td className="border border-slate-300 px-2 py-1.5">{item.assignedStaffName || '—'}</td>
                    <td className="border border-slate-300 px-2 py-1.5">{STATUS_LABEL[item.status]}</td>
                    <td className="border border-slate-300 px-2 py-1.5">{fmtDate(item.deadlineDate)}</td>
                    <td className="border border-slate-300 px-2 py-1.5">{item.remark || '—'}</td>
                  </tr>
                ))}
                {allActionItems.length === 0 && (
                  <tr><td colSpan={5} className="border border-slate-300 px-2 py-3 text-center text-slate-400">No action items.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {meeting.notes && (
            <div className="mt-6">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-800">Notes</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{meeting.notes}</p>
            </div>
          )}

          <div className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm">
            <span className="font-semibold text-indigo-700">Next Meeting:</span>{' '}
            {meeting.nextMeetingDate ? (
              <>{fmtDate(meeting.nextMeetingDate)}{meeting.nextMeetingTime ? ` · ${to12h(meeting.nextMeetingTime)}` : ''}</>
            ) : 'Not scheduled'}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-4 text-xs text-slate-500">
            <span>Minutes recorded by: {recordedBy}</span>
            <span>Generated {new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date())}</span>
          </div>
        </div>
      </div>
    </>
  );
}
